import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import React, { PropsWithChildren, useEffect } from 'react';

import { setBlogResults } from '@/redux/slices/blog.slice';

import { useAppDispatch } from '@/redux';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { StoreConfModel } from '@/models';
import { useServices } from '@/services/useServices';
import { UntypedObject, valueof } from '@/types';
import { firstTitleLetterToUpper, formatBreadcrumbData } from '@/utils/blogUtils';
import { scrollToPageTop } from '@/utils/scroll';

import { MetaTagsBlog } from '@/components/blog/MetaTagsBlog';
import SubMenu from '@/components/layout/SubMenu';

const BreadcrumbContainer = dynamic(() => import('@/components/common/BreadcrumbContainer'));
BreadcrumbContainer.displayName = 'BreadcrumbContainer';

import styles from './BlogLayout.module.scss';
import { BLOG_PAGE_TYPE } from '@/constants/blog';
import { PAGES } from '@/constants/pages';
import { PAGE_TYPE } from '@/constants/datalayer';

type Falsy = false | 0 | '' | null | undefined;

// TODO: Tester que la traduction fonctionne sur les autres pays que FR et EN
const getBreadcrumb = (
  breadcrumbData: string[], className: string, isHomePage: boolean, t: (key: string) => string
): Falsy | React.JSX.Element => {
  return !isHomePage && (
    <div className={className}>
      <BreadcrumbContainer
        homeLabel={t('breadcrumbHomeLabelBlog')}
        breadcrumbSeo={breadcrumbData}
        baseUrl={PAGES.BLOG}
      />
    </div>
  );
};

type BlogLayoutProps = {
  data: UntypedObject;
  componentType: valueof<typeof BLOG_PAGE_TYPE>
  slug: string;
  storeConf: StoreConfModel;
  isArchivePage?: boolean
}

export const BlogLayout = (
  { data, componentType, slug, children, isArchivePage = false, storeConf }: PropsWithChildren<BlogLayoutProps>
) => {
  const t = useTranslation().t;
  const dispatch = useAppDispatch();
  const { datalayerService } = useServices();
  const { mappedBreakpoint: breakpoint, isMobile } = useScreenSizeContext();
  const isHomePage = componentType === BLOG_PAGE_TYPE.BLOG_HOME;
  const archiveDate = isArchivePage && slug;

  useEffect(() => {
    if (isArchivePage) {
      datalayerService.pushPageName(PAGE_TYPE.BLOG_ARCHIVE, PAGE_TYPE.BLOG_ARCHIVE);
    }
  }, []);

  const breadcrumbClassName = [
    isMobile ? styles['breadcrumb-mobile'] : styles['breadcrumb-desktop'],
    !isMobile && styles['justify-center']
  ].filter(Boolean).join(' ');

  const blogLayoutClassName = [
    styles[`padding-${breakpoint}`],
    styles.container
  ].filter(Boolean).join(' ');

  const formatedBreadcrumb = formatBreadcrumbData(data?.breadcrumbSeo) || [];

  if (isArchivePage) {
    const title = firstTitleLetterToUpper(archiveDate, storeConf.locale);
    componentType = BLOG_PAGE_TYPE.BLOG_ARCHIVE;
    data.title = title;
    formatedBreadcrumb.push({ name: title });
  }

  useEffect(() => {
    // Without setTimeout, this scroll happens before the browser scroll sometimes
    setTimeout(() => scrollToPageTop(), 200);
  }, []);

  useEffect(() => {
    dispatch(setBlogResults({ blogResults: data.articlesResult }));
  }, [data.articlesResult]);

  return (
    <>
      <MetaTagsBlog
        storeConf={storeConf}
        type={componentType}
        content={data}
        slug={slug}
        currentPageUrl={slug || PAGES.BLOG_HOME_URL}
      />
      <SubMenu />
      <div className={blogLayoutClassName} data-rbf="blog-layout">
        {!isMobile && getBreadcrumb(formatedBreadcrumb, breadcrumbClassName, isHomePage, t)}
        {children}
        {isMobile && getBreadcrumb(formatedBreadcrumb, breadcrumbClassName, isHomePage, t)}
      </div>
    </>
  );
};

export default BlogLayout;
